import { useState } from 'react';
import axios from 'axios';
import { X, Bug, Loader2, AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* BugReportButton — small button shaped like a bug. Lives inside the
   Heavenly Assistant header so the Assistant is always one click away
   from "report a problem". On click opens a modal with three fields:
     • What's the problem?  (title)
     • Where in the app?    (location)
     • What's the issue?    (description)
   The modal also picks up the user agent + page URL automatically so
   admins see the context without having to ask. */
export default function BugReportButton({ token, currentLocation }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ title: '', location: currentLocation || '', description: '', severity: 'normal' });

  const reset = () => {
    setForm({ title: '', location: currentLocation || '', description: '', severity: 'normal' });
  };

  const submit = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      toast.error('Tell me what the problem is and what you saw.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bug-reports`, {
        title: form.title.trim(),
        description: form.description.trim(),
        location: form.location.trim() || (typeof window !== 'undefined' ? window.location.pathname : ''),
        severity: form.severity,
      }, token ? { headers: { Authorization: `Bearer ${token}` } } : {});
      toast.success('Thanks — every admin just got a notification 🙏');
      reset();
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit the report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        data-testid="bug-report-btn"
        onClick={() => setOpen(true)}
        className="p-1.5 rounded-lg transition hover:bg-white/15"
        title="Report a bug or lag — every admin gets pinged"
        aria-label="Report a bug"
      >
        <Bug className="w-4 h-4 text-white" />
      </button>

      {open && (
        <div data-testid="bug-report-modal" className="fixed inset-0 z-[150] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(15,23,42,0.7)', backdropFilter: 'blur(6px)' }}>
          <div className="w-full max-w-md rounded-2xl overflow-hidden shadow-2xl" style={{ backgroundColor: 'white', fontFamily: 'Manrope' }}>
            <div className="px-5 py-4 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)' }}>
              <div className="flex items-center gap-2">
                <Bug className="w-5 h-5 text-white" />
                <p className="text-sm font-bold text-white">Report a bug or lag</p>
              </div>
              <button data-testid="bug-report-close" onClick={() => setOpen(false)} className="p-1.5 rounded-lg text-white hover:bg-white/15">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <Field label="What's the problem?" required>
                <input
                  data-testid="bug-title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Map widgets disappear when zooming"
                  className="w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2"
                  style={{ borderColor: '#D6D0C0' }}
                  maxLength={200}
                />
              </Field>
              <Field label="Where in the app?">
                <input
                  data-testid="bug-location"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="e.g. /app · the interactive map · top-right pill"
                  className="w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2"
                  style={{ borderColor: '#D6D0C0' }}
                />
              </Field>
              <Field label="What did you see, and what did you expect?" required>
                <textarea
                  data-testid="bug-description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Tell me what happened. Steps to reproduce help a lot. Screenshots can be attached after submitting via the assistant chat."
                  rows={5}
                  className="w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2 resize-none"
                  style={{ borderColor: '#D6D0C0' }}
                  maxLength={4000}
                />
              </Field>
              <Field label="Severity">
                <div className="flex items-center gap-1.5" data-testid="bug-severity-row">
                  {[
                    { id: 'low',    label: 'Low',    icon: Info,           color: '#3B82F6' },
                    { id: 'normal', label: 'Normal', icon: AlertCircle,    color: '#F59E0B' },
                    { id: 'high',   label: 'High',   icon: AlertTriangle,  color: '#DC2626' },
                  ].map((s) => (
                    <button
                      key={s.id}
                      data-testid={`bug-severity-${s.id}`}
                      onClick={() => setForm({ ...form, severity: s.id })}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-bold transition"
                      style={{
                        backgroundColor: form.severity === s.id ? s.color : 'white',
                        color: form.severity === s.id ? 'white' : '#1A2E2E',
                        border: `1px solid ${form.severity === s.id ? s.color : '#D6D0C0'}`,
                      }}
                    >
                      <s.icon className="w-3 h-3" /> {s.label}
                    </button>
                  ))}
                </div>
              </Field>
            </div>
            <div className="px-5 py-3 flex justify-end gap-2 border-t" style={{ borderColor: '#E2DBC8', backgroundColor: '#F5F0E1' }}>
              <button onClick={() => setOpen(false)} disabled={submitting} className="px-4 py-2 rounded-lg text-sm font-bold" style={{ backgroundColor: 'white', border: '1px solid #D6D0C0', color: '#1A2E2E' }}>
                Cancel
              </button>
              <button
                data-testid="bug-submit"
                onClick={submit}
                disabled={submitting}
                className="px-5 py-2 rounded-lg text-sm font-bold text-white inline-flex items-center gap-2"
                style={{ background: 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)', opacity: submitting ? 0.6 : 1 }}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bug className="w-4 h-4" />}
                {submitting ? 'Sending…' : 'Send to Admins'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: '#1F4FBE' }}>
        {label}{required && <span style={{ color: '#DC2626' }}> *</span>}
      </span>
      {children}
    </label>
  );
}
